// web-v2/src/components/v3/doors.js
//
// WHICH DOOR A V3 FACT POSTS THROUGH. An AIRgus item names a fact_ref
// (subject, id, field); this file maps it to the ONE writer that already
// exists for that fact in components/account/record/factWriters.js and asks
// that file for the sender. It adds no writer and calls no endpoint itself
// (guard: tests/v3-account-page.test.js). A fact with no door here renders
// its state word and says so.
//
// term.policy_number is not a factWriters door: its writer is the v3 policies
// service, reached through v3.service setV3PolicyNumber from the input on the
// row (FactLine PolicyNumberInput, stage D2). PAGE_DOORS below is how an
// AIRgus remedy finds that input. The V2 PolicyInfoDialog writes the Zoho
// Deal copy, a different store from the one this page reads, and is not
// offered here.
import { inlineSender, doorLabel } from '@/components/account/record/factWriters.js';

const ACCOUNT_FIELDS = Object.freeze({
  address: { door: 'named_insured', key: 'address' },
  name: { door: 'named_insured', key: 'account_name' },
});
const AIRCRAFT_FIELDS = Object.freeze({
  year: 'year', make: 'make', model: 'model', seats: 'seats', serial_number: 'serial_number',
});
// PHF columns correctable through PATCH /phf/:id/field (the record's phf_field door).
// Exported for the PHF edit on the people card (shared/AnswerEdit.jsx, on the answer itself).
export const PERSON_FIELDS = new Set([
  'certificate_type', 'date_of_birth', 'medical_class', 'medical_date', 'last_flight_review_date',
  'basicmed_course_date', 'total_flight_hours', 'hours_last_12_months',
]);

// THE DOORS THIS PAGE ITSELF HOLDS (stage D2): an AIRgus opener whose field is
// an input on a row of this page. The remedy links to that row's anchor
// (anchorFor on the item's fact_ref), where the input is, and the input
// focuses itself when the hash names it. Nothing navigates away.
// link_renewal: the Link control on an expired term that a later term on its aircraft looks like it renews (2026-10-14).
export const PAGE_DOORS = Object.freeze({ set_policy_number: { subject: 'term', field: 'policy_number' }, link_renewal: { subject: 'term', field: 'succession' } });

// A person's identity posts through the person edit door (people-create-door
// C3), the same factWriters sender the client record uses, keyed on the
// person's Contact. A person with no Contact has no target yet.
export const PERSON_IDENTITY_FIELDS = new Set(['name', 'preferred_name', 'email', 'phone']);

// The element id a fact row carries, so a remedy can point at it.
export function anchorFor(ref) {
  return ref && ref.subject && ref.field ? `fact-${ref.subject}-${ref.id}-${ref.field}` : null;
}

export function editorKind(field) {
  if (/_date$|date_of_birth/.test(field)) return 'date';
  if (/hours|year|seats/.test(field)) return 'number';
  return 'text';
}

// The writer object factWriters understands, or null when no door exists.
export function writerFor(ref, { people = [], aircraft = [] } = {}) {
  if (!ref) return null;
  if (ref.subject === 'account' && ACCOUNT_FIELDS[ref.field]) return ACCOUNT_FIELDS[ref.field];
  if (ref.subject === 'aircraft' && AIRCRAFT_FIELDS[ref.field]) {
    const ac = aircraft.find((a) => a.id === ref.id);
    return ac ? { door: 'aircraft_identity', key: AIRCRAFT_FIELDS[ref.field], target: { tail: ac.registration } } : null;
  }
  if (ref.subject === 'person' && PERSON_FIELDS.has(ref.field)) {
    const p = people.find((x) => x.id === ref.id);
    const phfId = p?.pilot?.phf_id || null;
    return { door: 'phf_field', key: ref.field, target: phfId ? { module: 'pilot_history_forms', id: phfId } : null };
  }
  if (ref.subject === 'person' && PERSON_IDENTITY_FIELDS.has(ref.field)) {
    const p = people.find((x) => x.id === ref.id);
    return { door: 'person_identity', key: ref.field, target: p?.crm_contact_id ? { contact_id: String(p.crm_contact_id) } : null };
  }
  return null;
}

// { sender, label }: sender posts the value through the existing door; label
// is what the row says when it cannot post inline.
export function senderFor(ref, ctx) {
  const writer = writerFor(ref, ctx);
  if (!writer) return { sender: null, label: null, writer: null };
  return {
    writer,
    sender: inlineSender({ writer, accountId: ctx.account?.crm_account_id, accountName: ctx.account?.name }),
    label: doorLabel(writer, { hasOpenTerm: !!ctx.currentTermId }),
  };
}

// The ?door= keys the V2 term workspace opens: DealWorkspace's deep-link
// effect names seven of its own (present, quotes, fleet, money, roster, runway,
// experience_model) and
// then any useDealActions opener. This set is a COPY of those two literals;
// tests/v3-account-page.test.js pins it to both sources, so a key added or
// removed there fails the gate here rather than drifting.
export const DEAL_DOORS = new Set([
  'present', 'quotes', 'fleet', 'money', 'roster', 'runway', 'experience_model', 'pilot_answer',
  'send', 'decline', 'quote', 'paperwork', 'renew', 'binding', 'snkl', 'bor', 'lpr', 'coi', 'payment_request',
  'upload_docs', 'send_bundle', 'carrier_confirm', 'edit_roster', 'quote_diff', 'edit_insured', 'policy_info', 'correct_limits',
  'add_pilot', 'welcome_text', 'resume_link', 'rtq', 'rewrite', 'outcome', 'bind_hold',
]);

// THE PHONE BAR'S ITEMS (stage D4, the Mobile and MobileSections sketches'
// bottom nav: Next up, Search, Accounts, Money, in that order). Each item
// names what it OPENS, and phoneBarAction turns that into a sender only when
// the door exists on this page: a section the phone layout can scroll to or
// open (its openSection callback), the search palette (the one
// 'search-palette:open' event TopBar's button fires and V3SearchPalette
// listens for on /v3/ routes), or a route the router holds. An item whose
// door does not resolve renders disabled with NO_DOOR_HERE; the bar never
// invents a destination. `current` marks the sketch's highlighted tab: the
// page is an account, so Accounts is the family it sits in.
export const SEARCH_PALETTE_OPEN = 'search-palette:open';
export const PHONE_BAR_ITEMS = Object.freeze([
  { key: 'next', label: 'Next up', opens: { kind: 'section', section: 'next' } },
  { key: 'search', label: 'Search', opens: { kind: 'event', event: SEARCH_PALETTE_OPEN } },
  { key: 'accounts', label: 'Accounts', opens: { kind: 'route', to: '/search' }, current: true },
  { key: 'money', label: 'Money', opens: { kind: 'section', section: 'money' } },
]);

// { onClick } for a section or an event, { to } for a route, null when the
// door is absent (no openSection handed in, or an unknown kind).
export function phoneBarAction(item, { openSection } = {}) {
  const o = item?.opens;
  if (!o) return null;
  if (o.kind === 'section') return typeof openSection === 'function' ? { onClick: () => openSection(o.section) } : null;
  if (o.kind === 'event') return { onClick: () => window.dispatchEvent(new Event(o.event)) };
  if (o.kind === 'route') return o.to ? { to: o.to } : null;
  return null;
}

// A remedy links ONLY where a door will open at the other end: this page's
// own input, for a PAGE_DOORS opener whose fact_ref names a term of the
// account (the row anchor); or the term workspace, for a DEAL_DOORS opener,
// on a term that has a crm_deal_id. Anything else is null and the item says
// NO_DOOR_HERE beside its label. A link that lands on a page and opens
// nothing is a claim (the contract's own record_payment is not a door
// anywhere yet).
export function remedyHref(item, { account, terms = [] } = {}) {
  const opener = item?.remedy?.opener;
  if (!opener) return null;
  const page = PAGE_DOORS[opener];
  const ref = item.fact_ref;
  if (page && ref && ref.subject === page.subject && ref.field === page.field && terms.some((x) => x.id === ref.id)) return `#${anchorFor(ref)}`;
  if (!DEAL_DOORS.has(opener) || !account?.crm_account_id) return null;
  if (item.subject?.kind !== 'term') return null;
  const t = terms.find((x) => x.id === item.subject.id);
  if (!t?.crm_deal_id) return null;
  return `/account/${encodeURIComponent(account.crm_account_id)}/deal/${encodeURIComponent(t.crm_deal_id)}?door=${encodeURIComponent(opener)}`;
}
